import { useEffect, useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate }         from 'react-router-dom'
import { useTranslation }      from 'react-i18next'
import { useForm, Controller, useFieldArray } from 'react-hook-form'
import { useAuth } from '@/context/AuthContext'
import { listExams, createExam, updateExam, deleteExam, listQuestionBanks, getExam, getExamLeaderboard, resetExamAttempts, listGradingCriteria, createGradingCriteria, createQuestionBank, createQuestion, getExamResults, listGroups } from '@/api/resources'
import {
  Button, PageHeader, Badge, EmptyState, Spinner, FIELD_CLS, SearchInput, TableFooter,
} from '@/components/ui/primitives'
import { Plus, Pencil, Trash2, Eye, X, Trophy, BarChart2, ClipboardList, Users, Copy, Check, Send, FileDown, MoreVertical, Share2, HelpCircle } from 'lucide-react'
import ConfirmDialog from '@/components/ConfirmDialog'
import { exportExamPdf, PDF_MODES } from '@/utils/examPdf'
import { fetchAllPages } from '@/utils/fetchAll'

const PAGE_SIZE = 20

function fmt(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}

function formatShareDate(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  const pad = n => String(n).padStart(2, '0')
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function buildShareText(exam) {
  const lines = [exam.title]
  const dateStr = formatShareDate(exam.start_time)
  if (dateStr) lines.push(dateStr)
  const teacherName = exam.teacher?.full_name || exam.teacher?.username
  if (teacherName) lines.push(teacherName)
  const link = BOT_USERNAME
    ? `https://t.me/${BOT_USERNAME}?start=${exam.code}`
    : `${MAIN_URL}/exam/${exam.code}`
  lines.push(link)
  return lines.join('\n')
}

// ── SlideOver ─────────────────────────────────────────────────────────
function SlideOver({ open, title, onClose, closeOnBackdrop = true, children }) {
  if (!open) return null
  return (
    <>
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40" onClick={closeOnBackdrop ? onClose : undefined} />
      <div className="fixed top-0 right-0 h-full w-full max-w-2xl bg-white dark:bg-slate-900 shadow-2xl z-50 flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-slate-700 flex-shrink-0">
          <h2 className="text-base font-bold text-gray-900 dark:text-white">{title}</h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-500 dark:text-slate-400"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
      </div>
    </>
  )
}

// ── ToggleCard ────────────────────────────────────────────────────────
function ToggleCard({ label, hint, icon, checked, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`w-full flex items-start gap-3 p-3.5 rounded-xl border-2 text-left transition-all ${
        checked
          ? 'border-[var(--coral)] bg-[var(--coral-tint)]'
          : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600'
      }`}
    >
      <span className="text-xl leading-none mt-0.5 select-none">{icon}</span>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${checked ? 'text-[var(--coral)]' : 'text-gray-700 dark:text-slate-300'}`}>{label}</p>
        <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">{hint}</p>
      </div>
      <div className={`relative flex-shrink-0 mt-0.5 w-9 h-5 rounded-full transition-colors ${checked ? 'bg-[var(--coral)]' : 'bg-gray-300 dark:bg-slate-600'}`}>
        <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${checked ? 'left-4' : 'left-0.5'}`} />
      </div>
    </button>
  )
}

// ── StepBar ───────────────────────────────────────────────────────────
const STEP_LABELS = ['Asosiy', 'Vaqt', 'Savollar', "Ko'rib chiqish"]

function StepBar({ step }) {
  return (
    <div className="flex items-center mb-6">
      {STEP_LABELS.map((label, i) => {
        const n       = i + 1
        const done    = n < step
        const current = n === step
        return (
          <div key={n} className={`flex items-center ${i < STEP_LABELS.length - 1 ? 'flex-1' : ''}`}>
            <div className="flex flex-col items-center gap-1">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                done    ? 'bg-[var(--coral)] text-white' :
                current ? 'bg-[var(--coral)] text-white ring-4 ring-[var(--coral-tint)]' :
                          'bg-gray-100 dark:bg-slate-800 text-gray-400 dark:text-slate-500 border border-gray-200 dark:border-slate-600'
              }`}>
                {done ? '✓' : n}
              </div>
              <span className={`text-[10px] whitespace-nowrap ${current ? 'text-[var(--coral)] font-semibold' : 'text-gray-400 dark:text-slate-500'}`}>{label}</span>
            </div>
            {i < STEP_LABELS.length - 1 && (
              <div className={`flex-1 h-px mb-4 mx-1.5 transition-colors ${done ? 'bg-[var(--coral-soft)]' : 'bg-gray-200 dark:bg-slate-700'}`} />
            )}
          </div>
        )
      })}
    </div>
  )
}

// ── ModePicker ────────────────────────────────────────────────────────
function ModePicker({ onSelect }) {
  return (
    <div className="space-y-3 py-2">
      <p className="text-sm text-gray-500 dark:text-slate-400 mb-4">Imtihon yaratish usulini tanlang:</p>
      <button
        type="button"
        onClick={() => onSelect('bank')}
        className="w-full flex items-start gap-4 p-4 rounded-xl border-2 border-gray-200 dark:border-slate-700 hover:border-[var(--coral)] hover:bg-[var(--coral-tint)] text-left transition-all group"
      >
        <span className="text-2xl mt-0.5">📚</span>
        <div>
          <p className="font-semibold text-sm text-gray-800 dark:text-white group-hover:text-[var(--coral)]">Savollar bazasidan tanlash</p>
          <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">Mavjud savollar bazasini imtihonga ulash. Har bir bazadan nechta savol chiqishini sozlash mumkin.</p>
        </div>
      </button>
      <button
        type="button"
        onClick={() => onSelect('inline')}
        className="w-full flex items-start gap-4 p-4 rounded-xl border-2 border-gray-200 dark:border-slate-700 hover:border-[var(--coral)] hover:bg-[var(--coral-tint)] text-left transition-all group"
      >
        <span className="text-2xl mt-0.5">✏️</span>
        <div>
          <p className="font-semibold text-sm text-gray-800 dark:text-white group-hover:text-[var(--coral)]">Savollarni hozir yaratish</p>
          <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">Yangi savollar yozing — "Savollar bazasi" avtomatik yaratiladi va imtihonga bog'lanadi.</p>
        </div>
      </button>
    </div>
  )
}

// ── InlineQuestionCard ────────────────────────────────────────────────
const INLINE_TYPES = [
  { value: 1, label: 'Bir javobli (radio)' },
  { value: 2, label: 'Ko\'p javobli (checkbox)' },
  { value: 3, label: 'To\'g\'ri / Noto\'g\'ri' },
  { value: 4, label: 'Qisqa matn' },
  { value: 5, label: 'Insho' },
]

function newOption(id) { return { id, text: '' } }
function newQuestion() {
  return {
    _id: Date.now() + Math.random(),
    content: '',
    question_type: 1,
    score: 1,
    options: [newOption(1), newOption(2), newOption(3), newOption(4)],
    correct_single: 1,
    correct_multi: [],
    correct_tf: true,
  }
}

function buildInlinePayload(q) {
  const type = q.question_type
  const opts = q.options.filter(o => o.text.trim())
  switch (type) {
    case 1: return { question_type: 1, content: q.content, options: opts, correct_answer: { answer: q.correct_single }, score: q.score }
    case 2: return { question_type: 2, content: q.content, options: opts, correct_answer: { answers: q.correct_multi }, score: q.score }
    case 3: return { question_type: 3, content: q.content, options: [], correct_answer: { answer: q.correct_tf }, score: q.score }
    default: return { question_type: type, content: q.content, options: [], correct_answer: {}, score: q.score }
  }
}

function InlineQuestionCard({ question: q, number, onChange, onRemove }) {
  const type = q.question_type
  const hasOptions = type === 1 || type === 2

  function setOption(idx, text) {
    const opts = q.options.map((o, i) => i === idx ? { ...o, text } : o)
    onChange({ options: opts })
  }
  function addOption() {
    const nextId = Math.max(0, ...q.options.map(o => o.id)) + 1
    onChange({ options: [...q.options, newOption(nextId)] })
  }
  function removeOption(idx) {
    if (q.options.length <= 2) return
    onChange({ options: q.options.filter((_, i) => i !== idx) })
  }

  return (
    <div className="rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2.5 bg-gray-50 dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700">
        <span className="text-xs font-bold text-gray-500 dark:text-slate-400 w-6 text-center">{number}</span>
        <select
          value={type}
          onChange={e => onChange({ question_type: Number(e.target.value) })}
          className="field-base py-1 text-xs flex-1"
        >
          {INLINE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <div className="flex items-center gap-1">
          <label className="text-xs text-gray-500 dark:text-slate-400">Ball:</label>
          <input
            type="number" min="0.5" step="0.5" value={q.score}
            onChange={e => onChange({ score: Number(e.target.value) || 1 })}
            className="field-base py-1 w-16 text-xs"
          />
        </div>
        <button type="button" onClick={onRemove} className="p-1 text-gray-400 hover:text-red-500 transition-colors">
          <X size={14} />
        </button>
      </div>

      <div className="p-3 space-y-3">
        {/* Content */}
        <textarea
          rows={2}
          placeholder="Savol matnini kiriting..."
          value={q.content}
          onChange={e => onChange({ content: e.target.value })}
          className="field-base text-sm"
        />

        {/* Options (type 1 & 2) */}
        {hasOptions && (
          <div className="space-y-1.5">
            <p className="text-xs text-gray-500 dark:text-slate-400 font-medium">
              {type === 1 ? 'Variantlar (to\'g\'ri javobni belgilang):' : 'Variantlar (bir nechta to\'g\'rini belgilang):'}
            </p>
            {q.options.map((opt, idx) => (
              <div key={opt.id} className="flex items-center gap-2">
                {type === 1 ? (
                  <input
                    type="radio"
                    name={`correct_${q._id}`}
                    checked={q.correct_single === opt.id}
                    onChange={() => onChange({ correct_single: opt.id })}
                    className="accent-[var(--coral)] flex-shrink-0"
                  />
                ) : (
                  <input
                    type="checkbox"
                    checked={q.correct_multi.includes(opt.id)}
                    onChange={e => {
                      const cm = e.target.checked
                        ? [...q.correct_multi, opt.id]
                        : q.correct_multi.filter(id => id !== opt.id)
                      onChange({ correct_multi: cm })
                    }}
                    className="accent-[var(--coral)] flex-shrink-0"
                  />
                )}
                <input
                  type="text"
                  placeholder={`${idx + 1}-variant`}
                  value={opt.text}
                  onChange={e => setOption(idx, e.target.value)}
                  className="field-base text-sm flex-1"
                />
                <button type="button" onClick={() => removeOption(idx)} className="p-1 text-gray-300 hover:text-red-400 transition-colors disabled:opacity-30" disabled={q.options.length <= 2}>
                  <X size={12} />
                </button>
              </div>
            ))}
            <button type="button" onClick={addOption} className="text-xs text-[var(--coral)] hover:opacity-80 font-medium flex items-center gap-1 mt-1">
              <Plus size={12} /> Variant qo'shish
            </button>
          </div>
        )}

        {/* True/False (type 3) */}
        {type === 3 && (
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name={`tf_${q._id}`} checked={q.correct_tf === true} onChange={() => onChange({ correct_tf: true })} className="accent-[var(--coral)]" />
              <span className="text-sm text-gray-700 dark:text-slate-300">✅ To'g'ri</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name={`tf_${q._id}`} checked={q.correct_tf === false} onChange={() => onChange({ correct_tf: false })} className="accent-[var(--coral)]" />
              <span className="text-sm text-gray-700 dark:text-slate-300">❌ Noto'g'ri</span>
            </label>
          </div>
        )}

        {/* Short text / Essay hint */}
        {(type === 4 || type === 5) && (
          <p className="text-xs text-gray-400 dark:text-slate-500 bg-gray-50 dark:bg-slate-800 rounded-lg px-3 py-2">
            💡 Bu savol turi AI tomonidan baholanadi — to'g'ri javob belgilash shart emas.
          </p>
        )}
      </div>
    </div>
  )
}

// ── ExamForm ──────────────────────────────────────────────────────────
function ExamForm({ initial, allBanks, allCriteria, allGroups = [], setCriteria, defaultCriteriaId, onSaved, onCancel, t }) {
  const isEdit = !!initial
  const {
    register, handleSubmit, control, watch, trigger, setValue,
    formState: { isSubmitting, errors },
  } = useForm({
    shouldUnregister: false,
    defaultValues: {
      title:               initial?.title || '',
      description:         initial?.description || '',
      is_national_cert:    initial?.is_national_cert || false,
      is_public:           initial?.is_public || false,
      leaderboard_visible: initial?.leaderboard_visible || false,
      show_errors:         initial?.show_errors || false,
      start_time:             initial?.start_time ? initial.start_time.slice(0, 16) : '',
      end_time:               initial?.end_time ? initial.end_time.slice(0, 16) : '',
      time_limit_minutes:     initial?.time_limit_minutes || '',
      allowed_attempts:       initial?.allowed_attempts || 1,
      leaderboard_frozen_at:  initial?.leaderboard_frozen_at ? initial.leaderboard_frozen_at.slice(0, 16) : '',
      retry_from:             initial?.retry_from ? initial.retry_from.slice(0, 16) : '',
      grading_criteria:    initial?.grading_criteria != null ? String(initial.grading_criteria) : (isEdit ? '' : (defaultCriteriaId != null ? String(defaultCriteriaId) : '')),
      bank_configs:        initial?.banks?.map(b => ({
        question_bank:      String(b.question_bank),
        question_count:     b.question_count,
        score_per_question: b.score_per_question,
        order:              b.order ?? 0,
      })) || [],
      groups:              initial?.groups?.map(g => String(g)) || [],
    }
  })
  const { fields, append, remove } = useFieldArray({ control, name: 'bank_configs' })
  const [step,             setStep]             = useState(1)
  const [saveError,        setSaveError]        = useState(null)
  const [showNewCriteria,  setShowNewCriteria]  = useState(false)
  const [newCriteriaName,  setNewCriteriaName]  = useState('')
  const [savingCriteria,   setSavingCriteria]   = useState(false)
  const [questionMode,     setQuestionMode]     = useState(isEdit ? 'bank' : null)
  const [inlineQuestions,  setInlineQuestions]  = useState([newQuestion()])

  const formValues      = watch()
  const bankConfigs     = formValues.bank_configs || []
  const totalQ          = bankConfigs.reduce((s, bc) => s + (Number(bc.question_count)     || 0), 0)
  const totalScore      = bankConfigs.reduce((s, bc) => s + (Number(bc.question_count) || 0) * (Number(bc.score_per_question) || 0), 0)
  const selectedCriteria = allCriteria?.find(c => String(c.id) === String(formValues.grading_criteria))

  async function onSubmit(data) {
    setSaveError(null)
    try {
      let bankConfigs = (data.bank_configs || []).map((bc, i) => ({
        question_bank:      Number(bc.question_bank),
        question_count:     Number(bc.question_count),
        score_per_question: Number(bc.score_per_question),
        order:              i,
      }))

      // Mode 2: create question bank + questions automatically
      if (questionMode === 'inline') {
        const validQs = inlineQuestions.filter(q => q.content.trim())
        if (validQs.length === 0) { setSaveError('Kamida bitta savol kiriting.'); return }

        const { data: bank } = await createQuestionBank({
          name: `${data.title} — Savol bazasi`,
        })
        await Promise.all(
          validQs.map(q => createQuestion({ ...buildInlinePayload(q), question_bank: bank.id }))
        )
        const avgScore = validQs.reduce((s, q) => s + (q.score || 1), 0) / validQs.length
        bankConfigs = [{ question_bank: bank.id, question_count: validQs.length, score_per_question: Math.round(avgScore * 2) / 2, order: 0 }]
      }

      const payload = {
        ...data,
        time_limit_minutes:    data.time_limit_minutes ? Number(data.time_limit_minutes) : null,
        allowed_attempts:      Number(data.allowed_attempts),
        start_time:            data.start_time || null,
        end_time:              data.end_time || null,
        leaderboard_frozen_at: data.leaderboard_frozen_at || null,
        retry_from:            data.retry_from || null,
        grading_criteria:      data.grading_criteria ? Number(data.grading_criteria) : null,
        bank_configs:          bankConfigs,
        groups:                (data.groups || []).map(Number),
      }
      if (isEdit) await updateExam(initial.code, payload)
      else await createExam(payload)
      onSaved()
    } catch (err) {
      setSaveError(err.response?.data ? JSON.stringify(err.response.data) : err.message)
    }
  }

  async function goNext() {
    if (step === 1) {
      const valid = await trigger('title')
      if (!valid) return
    }
    if (step === 3 && fields.length > 0) {
      const keys = fields.flatMap((_, i) => [
        `bank_configs.${i}.question_bank`,
        `bank_configs.${i}.question_count`,
        `bank_configs.${i}.score_per_question`,
      ])
      const valid = await trigger(keys)
      if (!valid) return
    }
    setStep(s => s + 1)
  }

  // Mode not yet chosen (only for new exams)
  if (questionMode === null) {
    return (
      <div className="space-y-5">
        <ModePicker onSelect={mode => setQuestionMode(mode)} />
        <div className="flex justify-end pt-2 border-t border-gray-100 dark:border-slate-800">
          <Button variant="secondary" type="button" onClick={onCancel}>{t('common.cancel')}</Button>
        </div>
      </div>
    )
  }

  return (
    <form onSubmit={e => e.preventDefault()} className="space-y-5">
      <StepBar step={step} />

      {/* ── Qadam 1: Asosiy ─────────────────────────────────────── */}
      {step === 1 && (
        <div className="space-y-4">
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">{t('exams.examTitle')} *</label>
            <input {...register('title', { required: true })} className={FIELD_CLS} />
            {errors.title && <p className="text-xs text-red-500">{t('common.required')}</p>}
          </div>

          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">{t('exams.description')}</label>
            <textarea {...register('description')} rows={3} className={FIELD_CLS} />
          </div>

          <div className="space-y-2 pt-1">
            <Controller control={control} name="is_public" render={({ field }) => (
              <ToggleCard
                icon="🌐"
                label={t('exams.isPublic', 'Ommaviy imtihon')}
                hint="Barcha foydalanuvchilar ko'ra oladi va ishtirok eta oladi"
                checked={field.value}
                onChange={field.onChange}
              />
            )} />
            <Controller control={control} name="leaderboard_visible" render={({ field }) => (
              <ToggleCard
                icon="🏆"
                label={t('exams.leaderboardVisible', 'Reyting ommaviy')}
                hint="Top natijalar barcha talabalar uchun ko'rinadi"
                checked={field.value}
                onChange={field.onChange}
              />
            )} />
            <Controller control={control} name="is_national_cert" render={({ field }) => (
              <ToggleCard
                icon="🎓"
                label={t('exams.isNationalCert', 'Milliy sertifikat')}
                hint="Muvaffaqiyatli tugatganda rasmiy sertifikat beriladi"
                checked={field.value}
                onChange={field.onChange}
              />
            )} />
            <Controller control={control} name="show_errors" render={({ field }) => (
              <ToggleCard
                icon="🔍"
                label="Xatolarni ko'rsatish"
                hint="Imtihon tugagach o'quvchilar o'z xatolarini va to'g'ri javoblarni ko'ra oladi"
                checked={field.value}
                onChange={field.onChange}
              />
            )} />
          </div>

          {allGroups.length > 0 && (
            <div className="space-y-2 pt-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">
                Guruhlar <span className="text-xs font-normal text-gray-400 dark:text-slate-500">(bildirishnoma yuboriladi)</span>
              </label>
              <Controller control={control} name="groups" render={({ field }) => (
                <div className="grid grid-cols-2 gap-2">
                  {allGroups.map(g => {
                    const checked = field.value.includes(String(g.id))
                    return (
                      <label
                        key={g.id}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm transition-colors ${
                          checked
                            ? 'border-[var(--coral)] bg-orange-50 dark:bg-orange-900/20 text-gray-800 dark:text-slate-200'
                            : 'border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-400 hover:border-gray-300 dark:hover:border-slate-600'
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={checked}
                          onChange={() => {
                            const id = String(g.id)
                            field.onChange(
                              checked ? field.value.filter(v => v !== id) : [...field.value, id]
                            )
                          }}
                        />
                        <span className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center text-white text-xs transition-colors ${
                          checked ? 'bg-[var(--coral)] border-[var(--coral)]' : 'border-gray-300 dark:border-slate-600'
                        }`}>
                          {checked && '✓'}
                        </span>
                        <span className="truncate">{g.name}</span>
                      </label>
                    )
                  })}
                </div>
              )} />
            </div>
          )}
        </div>
      )}

      {/* ── Qadam 2: Vaqt ───────────────────────────────────────── */}
      {step === 2 && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">{t('exams.startTime')}</label>
              <input {...register('start_time')} type="datetime-local" className={FIELD_CLS} />
            </div>
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">{t('exams.endTime')}</label>
              <input {...register('end_time')} type="datetime-local" className={FIELD_CLS} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">{t('exams.timeLimit')}</label>
              <input {...register('time_limit_minutes')} type="number" min="0" className={FIELD_CLS} placeholder="daqiqa" />
            </div>
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">{t('exams.allowedAttempts')}</label>
              <input {...register('allowed_attempts')} type="number" min="1" className={FIELD_CLS} />
            </div>
          </div>

          <p className="text-xs text-gray-500 dark:text-slate-400 bg-gray-50 dark:bg-slate-800/50 rounded-lg px-3 py-2.5 leading-relaxed">
            ⏱ Vaqt chegarasi talaba imtihonni boshlagan paytdan hisoblanadi. Masalan, 90 daqiqa = 1 soat 30 daqiqa.
          </p>

          <div className="border-t border-gray-100 dark:border-slate-700 pt-4 space-y-3">
            <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">Reyting sozlamalari (ixtiyoriy)</p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">Reyting muzlatiladigan vaqt</label>
                <input {...register('leaderboard_frozen_at')} type="datetime-local" className={FIELD_CLS} />
                <p className="text-[11px] text-gray-400 dark:text-slate-500">Shu vaqtgacha bo'lgan natijalar asosiy reyting sifatida ko'rinadi</p>
              </div>
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">Qayta urinish vaqti</label>
                <input {...register('retry_from')} type="datetime-local" className={FIELD_CLS} />
                <p className="text-[11px] text-gray-400 dark:text-slate-500">Shu vaqtdan boshlab talabalar qayta topshira oladi</p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">
                Baholash mezoni
              </label>
              <button
                type="button"
                onClick={() => setShowNewCriteria(v => !v)}
                className="text-xs text-[var(--coral)] hover:opacity-80 font-medium flex items-center gap-1"
              >
                <Plus size={12} />
                Yangi mezon
              </button>
            </div>

            <select {...register('grading_criteria')} className={FIELD_CLS}>
              <option value="">— Mezon tanlang —</option>
              {(allCriteria || []).map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>

            {showNewCriteria && (
              <div className="flex items-center gap-2 p-3 bg-[var(--coral-tint)] border border-[var(--coral-soft)] rounded-lg">
                <input
                  type="text"
                  value={newCriteriaName}
                  onChange={e => setNewCriteriaName(e.target.value)}
                  placeholder="Yangi mezon nomi..."
                  className={FIELD_CLS + ' flex-1'}
                />
                <button
                  type="button"
                  disabled={!newCriteriaName.trim() || savingCriteria}
                  onClick={async () => {
                    setSavingCriteria(true)
                    try {
                      const { data } = await createGradingCriteria({ name: newCriteriaName.trim() })
                      setCriteria(prev => [...prev, data])
                      setValue('grading_criteria', data.id)
                      setNewCriteriaName('')
                      setShowNewCriteria(false)
                    } catch (err) {
                      alert(err.response?.data?.name?.[0] || err.message)
                    } finally {
                      setSavingCriteria(false)
                    }
                  }}
                  className="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap"
                >
                  {savingCriteria ? '...' : 'Saqlash'}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowNewCriteria(false); setNewCriteriaName('') }}
                  className="p-2 text-gray-400 hover:text-gray-600"
                >
                  <X size={15} />
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Qadam 3: Savollar ───────────────────────────────────── */}
      {step === 3 && questionMode === 'bank' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between bg-[var(--coral-tint)] border border-[var(--coral-soft)] rounded-xl px-4 py-2.5">
            <span className="text-sm font-medium text-[var(--coral)]">
              Jami: <strong>{totalQ}</strong> savol · <strong>{totalScore}</strong> ball
            </span>
            <Button type="button" variant="secondary" size="sm"
              onClick={() => append({ question_bank: '', question_count: 10, score_per_question: 1, order: fields.length })}>
              <Plus size={14} /> {t('common.add', 'Qo\'shish')}
            </Button>
          </div>

          {fields.length === 0 && (
            <p className="text-sm text-gray-400 dark:text-slate-500 text-center py-6">{t('exams.noBanks', 'Savollar bazasi tanlanmagan')}</p>
          )}

          <div className="space-y-2">
            {fields.map((field, idx) => {
              const selectedBankId = watch(`bank_configs.${idx}.question_bank`)
              const selectedBank   = allBanks.find(b => String(b.id) === String(selectedBankId))
              const maxCount       = selectedBank?.question_count ?? undefined
              return (
                <div key={field.id} className="flex items-end gap-2 bg-gray-50 dark:bg-slate-800/50 rounded-xl border border-gray-200 dark:border-slate-700 p-3">
                  <div className="flex-1 space-y-1">
                    <label className="block text-xs font-medium text-gray-500 dark:text-slate-400">{t('nav.questionBanks', 'Baza')}</label>
                    <select {...register(`bank_configs.${idx}.question_bank`, { required: true })} className={FIELD_CLS}>
                      <option value="">— Tanlang —</option>
                      {allBanks.map(b => <option key={b.id} value={b.id}>{b.name} ({b.question_count} savol)</option>)}
                    </select>
                  </div>
                  <div className="w-28 space-y-1">
                    <label className="block text-xs font-medium text-gray-500 dark:text-slate-400">
                      Soni {maxCount !== undefined && <span className="text-gray-400">(max {maxCount})</span>}
                    </label>
                    <input
                      {...register(`bank_configs.${idx}.question_count`, {
                        required: true, min: 1,
                        max: maxCount,
                        valueAsNumber: true,
                      })}
                      type="number" min="1" max={maxCount}
                      className={`${FIELD_CLS}${errors?.bank_configs?.[idx]?.question_count ? ' border-red-400' : ''}`}
                    />
                    {errors?.bank_configs?.[idx]?.question_count && (
                      <p className="text-xs text-red-500">Max: {maxCount}</p>
                    )}
                  </div>
                  <div className="w-24 space-y-1">
                    <label className="block text-xs font-medium text-gray-500 dark:text-slate-400">Ball</label>
                    <input {...register(`bank_configs.${idx}.score_per_question`, { required: true, min: 0.5 })} type="number" min="0.5" step="0.5" className={FIELD_CLS} />
                  </div>
                  <button type="button" onClick={() => remove(idx)} className="p-2 text-gray-400 hover:text-red-500"><Trash2 size={15} /></button>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {step === 3 && questionMode === 'inline' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between bg-[var(--coral-tint)] border border-[var(--coral-soft)] rounded-xl px-4 py-2.5">
            <span className="text-sm font-medium text-[var(--coral)]">
              Jami: <strong>{inlineQuestions.filter(q => q.content.trim()).length}</strong> savol
              {' '}· Baza avtomatik yaratiladi
            </span>
            <Button type="button" variant="secondary" size="sm"
              onClick={() => setInlineQuestions(prev => [...prev, newQuestion()])}>
              <Plus size={14} /> Savol qo'shish
            </Button>
          </div>

          {inlineQuestions.length === 0 && (
            <p className="text-sm text-gray-400 dark:text-slate-500 text-center py-6">Hali savol qo'shilmagan</p>
          )}

          <div className="space-y-2">
            {inlineQuestions.map((q, idx) => (
              <InlineQuestionCard
                key={q._id}
                question={q}
                number={idx + 1}
                onChange={patch => setInlineQuestions(prev => prev.map((item, i) => i === idx ? { ...item, ...patch } : item))}
                onRemove={() => setInlineQuestions(prev => prev.filter((_, i) => i !== idx))}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── Qadam 4: Ko'rib chiqish ─────────────────────────────── */}
      {step === 4 && (
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-slate-800/50 rounded-xl border border-gray-200 dark:border-slate-700 divide-y divide-gray-100 dark:divide-slate-700 overflow-hidden">
            <div className="px-4 py-3">
              <p className="text-xs text-gray-400 dark:text-slate-500 uppercase font-medium mb-1">{t('exams.examTitle')}</p>
              <p className="text-sm font-semibold text-gray-800 dark:text-white">{formValues.title}</p>
              {formValues.description && <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">{formValues.description}</p>}
            </div>

            <div className="px-4 py-3 flex flex-wrap gap-2">
              {formValues.is_public           && <Badge variant="green">🌐 Ommaviy</Badge>}
              {formValues.leaderboard_visible  && <Badge variant="amber">🏆 Reyting ommaviy</Badge>}
              {formValues.is_national_cert     && <Badge variant="purple">🎓 Milliy sertifikat</Badge>}
              {!formValues.is_public && !formValues.leaderboard_visible && !formValues.is_national_cert && (
                <span className="text-xs text-gray-400">—</span>
              )}
            </div>

            <div className="px-4 py-3 grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-xs text-gray-400 dark:text-slate-500">{t('exams.startTime')}</p>
                <p className="font-medium text-gray-800 dark:text-white">{formValues.start_time ? fmt(formValues.start_time) : '—'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 dark:text-slate-500">{t('exams.endTime')}</p>
                <p className="font-medium text-gray-800 dark:text-white">{formValues.end_time ? fmt(formValues.end_time) : '—'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 dark:text-slate-500">{t('exams.timeLimit')}</p>
                <p className="font-medium text-gray-800 dark:text-white">{formValues.time_limit_minutes ? `${formValues.time_limit_minutes} daqiqa` : '—'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 dark:text-slate-500">{t('exams.allowedAttempts')}</p>
                <p className="font-medium text-gray-800 dark:text-white">{formValues.allowed_attempts}</p>
              </div>
              {formValues.leaderboard_frozen_at && (
                <div>
                  <p className="text-xs text-gray-400 dark:text-slate-500">Reyting muzlatiladigan vaqt</p>
                  <p className="font-medium text-gray-800 dark:text-white">{fmt(formValues.leaderboard_frozen_at)}</p>
                </div>
              )}
              {formValues.retry_from && (
                <div>
                  <p className="text-xs text-gray-400 dark:text-slate-500">Qayta urinish vaqti</p>
                  <p className="font-medium text-gray-800 dark:text-white">{fmt(formValues.retry_from)}</p>
                </div>
              )}
            </div>

            {selectedCriteria && (
              <div className="px-4 py-3">
                <p className="text-xs text-gray-400 dark:text-slate-500 uppercase font-medium mb-1">{t('grading.createNew', 'Baholash mezoni')}</p>
                <p className="text-sm text-gray-800 dark:text-white">{selectedCriteria.name}</p>
              </div>
            )}

            {questionMode === 'bank' && bankConfigs.length > 0 && (
              <div className="px-4 py-3">
                <p className="text-xs text-gray-400 dark:text-slate-500 uppercase font-medium mb-2">{t('nav.questionBanks', 'Savollar bazalari')}</p>
                <div className="space-y-1.5">
                  {bankConfigs.map((bc, i) => {
                    const bank = allBanks.find(b => String(b.id) === String(bc.question_bank))
                    return bank ? (
                      <div key={i} className="flex justify-between text-sm">
                        <span className="text-gray-700 dark:text-slate-300">{bank.name}</span>
                        <span className="text-gray-500 dark:text-slate-400">{bc.question_count} × {bc.score_per_question} ball</span>
                      </div>
                    ) : null
                  })}
                </div>
                <p className="text-xs text-[var(--coral)] font-semibold mt-2.5 pt-2 border-t border-gray-200 dark:border-slate-700">
                  Jami: {totalQ} savol · {totalScore} ball
                </p>
              </div>
            )}

            {questionMode === 'inline' && (
              <div className="px-4 py-3">
                <p className="text-xs text-gray-400 dark:text-slate-500 uppercase font-medium mb-2">Savollar (avtomatik baza)</p>
                <div className="space-y-1">
                  {inlineQuestions.filter(q => q.content.trim()).map((q, i) => (
                    <div key={q._id} className="flex justify-between text-sm">
                      <span className="text-gray-700 dark:text-slate-300 truncate max-w-[70%]">{i + 1}. {q.content}</span>
                      <span className="text-gray-500 dark:text-slate-400 flex-shrink-0">{INLINE_TYPES.find(t => t.value === q.question_type)?.label?.split(' ')[0]} · {q.score} ball</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-[var(--coral)] font-semibold mt-2.5 pt-2 border-t border-gray-200 dark:border-slate-700">
                  Jami: {inlineQuestions.filter(q => q.content.trim()).length} savol · Baza: "{formValues.title} — Savol bazasi"
                </p>
              </div>
            )}
          </div>

          {saveError && (
            <p className="text-xs text-red-600 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 px-3 py-2 rounded-lg break-all">{saveError}</p>
          )}
        </div>
      )}

      {/* ── Navigatsiya ─────────────────────────────────────────── */}
      <div className="flex justify-between gap-3 pt-2 border-t border-gray-100 dark:border-slate-800">
        <Button variant="secondary" type="button" onClick={step === 1 ? onCancel : () => setStep(s => s - 1)}>
          {step === 1 ? t('common.cancel') : `← ${t('common.back', 'Orqaga')}`}
        </Button>
        {step < 4 ? (
          <Button type="button" onClick={goNext}>
            {t('common.next', 'Keyingi')} →
          </Button>
        ) : (
          <Button type="button" loading={isSubmitting} onClick={handleSubmit(onSubmit)}>
            {isEdit ? t('common.save') : t('exams.createNew')}
          </Button>
        )}
      </div>
    </form>
  )
}

// ── LeaderboardPanel ─────────────────────────────────────────────────
function LeaderboardPanel({ exam, t, onClose }) {
  const [rows,    setRows]    = useState(null)
  const [err,     setErr]     = useState(null)
  const [resetting, setResetting] = useState(null) // student_id being reset

  useEffect(() => {
    getExamLeaderboard(exam.code)
      .then(({ data }) => setRows(data.entries ?? data))
      .catch(e => setErr(e.response?.data?.detail || e.message))
  }, [exam.code])

  async function handleReset(studentId) {
    if (!window.confirm(t('exams.resetConfirm', 'Bu talabaning barcha urinishlari o\'chiriladi. Davom etasizmi?'))) return
    setResetting(studentId)
    try {
      await resetExamAttempts(exam.code, { student_id: studentId })
      // Refresh leaderboard
      const { data } = await getExamLeaderboard(exam.code)
      setRows(data.entries ?? data)
    } catch (e) {
      alert(e.response?.data?.detail || e.message)
    } finally {
      setResetting(null)
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <p className="text-xs font-medium text-gray-500 dark:text-slate-400 uppercase mb-1">
          {exam.title}
        </p>
        <p className="text-sm text-gray-600 dark:text-slate-300">
          {exam.leaderboard_visible
            ? <span className="text-green-600 font-medium">🌐 {t('exams.leaderboardPublic', 'Reyting hamma uchun ko\'rinadigan')}</span>
            : <span className="text-gray-400">🔒 {t('exams.leaderboardPrivate', 'Reyting yashirin (faqat admin)')}</span>
          }
        </p>
      </div>

      {err && <p className="text-sm text-red-500">{err}</p>}

      {rows === null && !err && (
        <div className="flex justify-center py-8"><Spinner /></div>
      )}

      {rows && rows.length === 0 && (
        <p className="text-sm text-gray-400 text-center py-6">{t('exams.leaderboardEmpty', 'Hali natijalar yo\'q')}</p>
      )}

      {rows && rows.length > 0 && (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-gray-500 dark:text-slate-400 uppercase border-b border-gray-100 dark:border-slate-700">
              <th className="text-left py-2 w-10">{t('exams.rank', 'O\'rin')}</th>
              <th className="text-left py-2">{t('exams.student', 'Talaba')}</th>
              <th className="text-right py-2">{t('exams.score', 'Ball')}</th>
              <th className="text-right py-2">{t('exams.correctCount', "To'g'ri")}</th>
              <th className="text-right py-2">{t('exams.finishedAt', 'Tugatilgan')}</th>
              <th className="text-right py-2">{t('common.actions', 'Amal')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.id} className="border-b border-gray-50 dark:border-slate-800 hover:bg-gray-50 dark:hover:bg-slate-800/50">
                <td className="py-2">
                  {row.rank === 1 ? '🥇' : row.rank === 2 ? '🥈' : row.rank === 3 ? '🥉' : (
                    <span className="text-gray-400 font-mono">{row.rank}</span>
                  )}
                </td>
                <td className="py-2 font-medium text-gray-800 dark:text-white">{row.student_name}</td>
                <td className="py-2 text-right text-[var(--coral)] font-semibold">{row.total_score}</td>
                <td className="py-2 text-right text-gray-500 dark:text-slate-400">{row.correct_count ?? '—'}</td>
                <td className="py-2 text-right text-gray-400 text-xs">{fmt(row.finished_at)}</td>
                <td className="py-2 text-right">
                  <button
                    onClick={() => handleReset(row.student_id)}
                    disabled={resetting === row.student_id}
                    className="text-xs px-2 py-1 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 disabled:opacity-50"
                    title={t('exams.resetAttempts', 'Urinishlarni tiklash')}
                  >
                    {resetting === row.student_id ? '…' : '🔄'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="flex justify-end pt-3 border-t border-gray-100 dark:border-slate-800">
        <Button variant="secondary" onClick={onClose}>{t('common.close', 'Yopish')}</Button>
      </div>
    </div>
  )
}

// ── ResultsPanel ─────────────────────────────────────────────────────
const Q_TYPE_LABEL = { 1: 'Bir javobli', 2: "Ko'p javobli", 3: 'Ha/Yo\'q', 4: 'Qisqa', 5: 'Esse', 6: "Bo'sh to'ldirish", 7: 'Moslashtirish' }

function StudentResultDetail({ result }) {
  const [open, setOpen] = useState(false)
  const snapshot     = result.questions_snapshot || []
  const answersData  = result.answers_data || {}
  const score        = parseFloat(result.total_score ?? 0)
  const maxScore     = snapshot.reduce((s, q) => s + parseFloat(q.score ?? 1), 0)
  const pct          = maxScore > 0 ? Math.round((score / maxScore) * 100) : 0

  function optText(options, id) {
    const opt = (options || []).find(o => o.id === id || o.id === Number(id))
    return opt?.text ?? `#${id}`
  }

  function renderAnswer(q, entry) {
    const type = q.question_type
    const ans  = entry?.answer
    if (ans === null || ans === undefined) return <span style={{ color: 'var(--ink-3)', fontStyle: 'italic' }}>—</span>
    if (type === 3) return <span>{ans ? 'Ha' : "Yo'q"}</span>
    if (type === 2 && Array.isArray(ans)) return ans.map(id => optText(q.options, id)).join(', ')
    if (type === 1) return optText(q.options, ans)
    return String(ans)
  }

  return (
    <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)', background: 'var(--surface)' }}>
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-[var(--bg-soft)]"
      >
        <div className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0"
          style={{ background: 'var(--lav-soft)', color: 'var(--lav)' }}>
          {result.student?.full_name?.[0] ?? result.student?.username?.[0] ?? '?'}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold m-0 truncate" style={{ color: 'var(--ink)' }}>
            {result.student?.full_name || result.student?.username}
          </p>
          <p className="text-[11px] m-0" style={{ color: 'var(--ink-3)' }}>{fmt(result.finished_at)}</p>
        </div>
        <div className="text-right flex-shrink-0">
          <span className="font-mono font-bold text-sm" style={{ color: 'var(--coral)' }}>{score}</span>
          <span className="text-[11px]" style={{ color: 'var(--ink-3)' }}> / {maxScore} ({pct}%)</span>
        </div>
        {result.grade_label && <Badge variant="blue">{result.grade_label}</Badge>}
        <span style={{ color: 'var(--ink-3)', fontSize: 12 }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && snapshot.length > 0 && (
        <div className="border-t px-4 py-3 space-y-2" style={{ borderColor: 'var(--border)', background: 'var(--bg)' }}>
          {snapshot.map((q, qi) => {
            const entry    = answersData[String(q.id)] ?? {}
            const earned   = parseFloat(entry.score ?? 0)
            const maxQ     = parseFloat(q.score ?? 1)
            const correct  = earned >= maxQ
            const partial  = !correct && earned > 0

            return (
              <div key={q.id} className="rounded-lg border px-3 py-2.5" style={{
                borderColor: correct ? 'var(--mint-soft)' : partial ? 'var(--butter-soft)' : 'var(--danger-soft)',
                background:  correct ? 'var(--mint-tint)' : partial ? 'var(--butter-tint)' : 'var(--danger-tint)',
              }}>
                <div className="flex items-start gap-2">
                  <span className="text-[11px] font-mono mt-0.5 flex-shrink-0" style={{ color: 'var(--ink-3)' }}>{qi + 1}.</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-[12.5px] font-medium m-0 mb-1" style={{ color: 'var(--ink)' }}>{q.content}</p>
                    <div className="flex items-center gap-4 flex-wrap">
                      <span className="text-[11px]" style={{ color: 'var(--ink-3)' }}>
                        {Q_TYPE_LABEL[q.question_type] ?? q.question_type}
                      </span>
                      <span className="text-[11px]" style={{ color: 'var(--ink-2)' }}>
                        Javob: <strong>{renderAnswer(q, entry)}</strong>
                      </span>
                    </div>
                  </div>
                  <span className="font-mono text-[12px] font-bold flex-shrink-0"
                    style={{ color: correct ? 'var(--mint)' : partial ? 'var(--warn)' : 'var(--danger)' }}>
                    {earned}/{maxQ}
                  </span>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function ResultsPanel({ exam, t, onClose }) {
  const [results, setResults] = useState(null)
  const [err,     setErr]     = useState(null)

  useEffect(() => {
    getExamResults(exam.code)
      .then(({ data }) => setResults(Array.isArray(data) ? data : (data.results ?? [])))
      .catch(e => setErr(e.response?.data?.detail || e.message))
  }, [exam.code])

  const finished = (results || []).filter(r => r.is_finished)

  return (
    <div className="space-y-4">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider mb-0.5" style={{ color: 'var(--ink-3)' }}>
          {exam.title}
        </p>
        <p className="text-sm" style={{ color: 'var(--ink-2)' }}>
          {finished.length} ta o'quvchi imtihonni tugatgan
        </p>
      </div>

      {err && <p className="text-sm" style={{ color: 'var(--danger)' }}>{err}</p>}
      {results === null && !err && <div className="flex justify-center py-8"><Spinner /></div>}
      {results !== null && finished.length === 0 && (
        <p className="text-sm text-center py-6" style={{ color: 'var(--ink-3)' }}>Hali natijalar yo'q</p>
      )}

      {finished.length > 0 && (
        <div className="space-y-2">
          {finished.map(r => <StudentResultDetail key={r.id} result={r} />)}
        </div>
      )}

      <div className="flex justify-end pt-3 border-t" style={{ borderColor: 'var(--border)' }}>
        <Button variant="secondary" onClick={onClose}>{t('common.close', 'Yopish')}</Button>
      </div>
    </div>
  )
}


// ── PDF export mode menu ──────────────────────────────────────────────
const PDF_MODE_OPTIONS = [
  { key: PDF_MODES.NO_ANSWERS, label: "Javobsiz", hint: "Faqat savollar, to'g'ri javobsiz" },
  { key: PDF_MODES.INLINE,     label: "Savol + javob", hint: "Har bir savol ostida to'g'ri javob" },
  { key: PDF_MODES.APPENDIX,   label: "Javoblar oxirida", hint: "Javoblar alohida sahifada, oxirida" },
]

const PDF_MENU_W = 240

function PdfExportMenu({ exam, iconOnly, className }) {
  const [open, setOpen]       = useState(false)
  const [loading, setLoading] = useState(false)
  const [pos, setPos]         = useState(null)
  const btnRef = useRef(null)
  const menuRef = useRef(null)

  useEffect(() => {
    if (!open) return
    function onDocClick(e) {
      if (btnRef.current?.contains(e.target)) return
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [open])

  function toggleOpen() {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect()
      const menuH = 170
      const openUp = rect.bottom + menuH > window.innerHeight
      setPos({
        left: Math.min(Math.max(rect.right - PDF_MENU_W, 8), window.innerWidth - PDF_MENU_W - 8),
        top: openUp ? rect.top - menuH - 4 : rect.bottom + 4,
      })
    }
    setOpen(o => !o)
  }

  async function handleSelect(mode) {
    setOpen(false)
    setLoading(true)
    try { await exportExamPdf(exam, mode) } finally { setLoading(false) }
  }

  return (
    <>
      {iconOnly ? (
        <button ref={btnRef} onClick={toggleOpen} disabled={loading} className="tbl-btn tbl-edit" title="PDF export">
          <FileDown size={15} />
        </button>
      ) : (
        <span ref={btnRef} className={`inline-block ${className || ''}`}>
          <Button variant="secondary" onClick={toggleOpen} disabled={loading} className="w-full">
            {loading
              ? <><div className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" /> Yuklanmoqda</>
              : <><FileDown size={14} /> PDF</>}
          </Button>
        </span>
      )}
      {open && pos && createPortal(
        <div ref={menuRef} className="fixed z-50 rounded-xl border shadow-lg py-1"
          style={{ background: 'var(--surface)', borderColor: 'var(--border)', width: PDF_MENU_W, left: pos.left, top: pos.top }}>
          {PDF_MODE_OPTIONS.map(opt => (
            <button key={opt.key} type="button" onClick={() => handleSelect(opt.key)}
              className="w-full text-left px-3 py-2 transition-colors"
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-soft)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <p className="text-sm font-medium" style={{ color: 'var(--ink)' }}>{opt.label}</p>
              <p className="text-xs" style={{ color: 'var(--ink-3)' }}>{opt.hint}</p>
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  )
}

// ── Row actions dropdown (table row "Amallar" column) ────────────────
const ROW_MENU_W = 240

function RowActionsMenu({ exam, onView, onResults, onLeaderboard, onEdit, onDelete }) {
  const navigate = useNavigate()
  const [open, setOpen]           = useState(false)
  const [pos, setPos]             = useState(null)
  const [pdfLoading, setPdfLoading] = useState(false)
  const [tgCopied, setTgCopied]   = useState(false)
  const [shareCopied, setShareCopied] = useState(false)
  const [questionsLoading, setQuestionsLoading] = useState(false)
  const btnRef  = useRef(null)
  const menuRef = useRef(null)

  useEffect(() => {
    if (!open) return
    function onDocClick(e) {
      if (btnRef.current?.contains(e.target)) return
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [open])

  function toggleOpen() {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect()
      const menuH = 420
      const openUp = rect.bottom + menuH > window.innerHeight
      setPos({
        left: Math.min(Math.max(rect.right - ROW_MENU_W, 8), window.innerWidth - ROW_MENU_W - 8),
        top: openUp ? Math.max(8, rect.top - menuH - 4) : rect.bottom + 4,
      })
    }
    setOpen(o => !o)
  }

  function run(fn) {
    setOpen(false)
    fn?.()
  }

  async function handlePdf(mode) {
    setOpen(false)
    setPdfLoading(true)
    try { await exportExamPdf(exam, mode) } finally { setPdfLoading(false) }
  }

  async function handleViewQuestions() {
    setOpen(false)
    setQuestionsLoading(true)
    try {
      let banksData = exam.banks
      if (!banksData) {
        const { data } = await getExam(exam.code)
        banksData = data.banks
      }
      const bankIds = (banksData || []).map(b => b.question_bank).filter(Boolean)
      if (!bankIds.length) return
      navigate(`/questions?banks=${bankIds.join(',')}&examTitle=${encodeURIComponent(exam.title)}`)
    } finally {
      setQuestionsLoading(false)
    }
  }

  function handleCopyTg(e) {
    e.stopPropagation()
    const link = `https://t.me/${BOT_USERNAME}?start=${exam.code}`
    navigator.clipboard.writeText(link).then(() => {
      setTgCopied(true)
      setTimeout(() => setTgCopied(false), 1500)
    })
  }

  async function handleShare(e) {
    e.stopPropagation()
    const text = buildShareText(exam)
    if (navigator.share) {
      setOpen(false)
      try { await navigator.share({ title: exam.title, text }) } catch { /* cancelled */ }
      return
    }
    navigator.clipboard.writeText(text).then(() => {
      setShareCopied(true)
      setTimeout(() => setShareCopied(false), 1500)
    })
  }

  const itemCls = 'w-full flex items-center gap-2.5 text-left px-3 py-2 text-sm transition-colors'
  const hoverProps = {
    onMouseEnter: e => e.currentTarget.style.background = 'var(--bg-soft)',
    onMouseLeave: e => e.currentTarget.style.background = 'transparent',
  }

  return (
    <>
      <button ref={btnRef} onClick={toggleOpen} disabled={pdfLoading} className="tbl-btn tbl-edit" title="Amallar">
        <MoreVertical size={15} />
      </button>
      {open && pos && createPortal(
        <div ref={menuRef} className="fixed z-50 rounded-xl border shadow-lg py-1 overflow-y-auto"
          style={{ background: 'var(--surface)', borderColor: 'var(--border)', width: ROW_MENU_W, left: pos.left, top: pos.top, maxHeight: '80vh' }}>
          <button type="button" {...hoverProps} className={itemCls} style={{ color: 'var(--ink)' }} onClick={() => run(onView)}>
            <Eye size={15} /> Ko'rish
          </button>
          <button type="button" {...hoverProps} className={itemCls} style={{ color: 'var(--ink)' }}
            disabled={questionsLoading} onClick={handleViewQuestions}>
            <HelpCircle size={15} /> Savollar
          </button>
          <button type="button" {...hoverProps} className={itemCls} style={{ color: 'var(--ink)' }} onClick={() => run(onResults)}>
            <Users size={15} /> O'quvchilar javoblari
          </button>
          <button type="button" {...hoverProps} className={itemCls} style={{ color: 'var(--ink)' }} onClick={() => run(onLeaderboard)}>
            <Trophy size={15} /> Reyting
          </button>
          <button type="button" {...hoverProps} className={itemCls} style={{ color: 'var(--ink)' }}
            onClick={() => run(() => navigate(`/analytics?exam=${exam.code}`))}>
            <BarChart2 size={15} /> Analitika
          </button>
          <button type="button" {...hoverProps} className={itemCls}
            style={{ color: shareCopied ? 'var(--mint, #34d399)' : 'var(--ink)' }} onClick={handleShare}>
            {shareCopied ? <Check size={15} /> : <Share2 size={15} />} {shareCopied ? 'Nusxalandi!' : 'Ulashish'}
          </button>

          <div className="my-1 border-t" style={{ borderColor: 'var(--border)' }} />
          <p className="px-3 pt-1 pb-0.5 text-[10px] font-semibold uppercase tracking-wide" style={{ color: 'var(--ink-3)' }}>PDF eksport</p>
          {PDF_MODE_OPTIONS.map(opt => (
            <button key={opt.key} type="button" {...hoverProps} className={itemCls} style={{ color: 'var(--ink)' }}
              disabled={pdfLoading} onClick={() => handlePdf(opt.key)}>
              <FileDown size={15} /> {opt.label}
            </button>
          ))}

          {BOT_USERNAME && exam.code && (
            <>
              <div className="my-1 border-t" style={{ borderColor: 'var(--border)' }} />
              <button type="button" {...hoverProps} className={itemCls}
                style={{ color: tgCopied ? 'var(--mint, #34d399)' : '#2ba5e0' }} onClick={handleCopyTg}>
                {tgCopied ? <Check size={15} /> : <Send size={15} />} {tgCopied ? 'Nusxalandi!' : 'Telegram havolasini nusxalash'}
              </button>
            </>
          )}

          <div className="my-1 border-t" style={{ borderColor: 'var(--border)' }} />
          <button type="button" {...hoverProps} className={itemCls} style={{ color: 'var(--ink)' }} onClick={() => run(onEdit)}>
            <Pencil size={15} /> Tahrirlash
          </button>
          <button type="button" className={itemCls} style={{ color: '#ef4444' }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.08)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            onClick={() => run(onDelete)}>
            <Trash2 size={15} /> O'chirish
          </button>
        </div>,
        document.body
      )}
    </>
  )
}

// ── ExamView (read-only) ─────────────────────────────────────────────
function ExamView({ exam, t, onClose, onEdit, onResults }) {
  const navigate = useNavigate()

  function handleViewQuestions() {
    const bankIds = (exam.banks || []).map(b => b.question_bank).filter(Boolean)
    if (!bankIds.length) return
    navigate(`/questions?banks=${bankIds.join(',')}&examTitle=${encodeURIComponent(exam.title)}`)
  }

  return (
    <div className="space-y-4">
      <div><p className="text-xs font-medium text-gray-500 dark:text-slate-400 uppercase mb-1">{t('exams.examTitle')}</p><p className="text-sm font-semibold text-gray-800 dark:text-white">{exam.title}</p></div>
      {exam.description && <div><p className="text-xs font-medium text-gray-500 dark:text-slate-400 uppercase mb-1">{t('exams.description')}</p><p className="text-sm text-gray-700 dark:text-slate-300">{exam.description}</p></div>}
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div><span className="text-gray-500 dark:text-slate-400">{t('exams.startTime')}:</span> <span className="text-gray-800 dark:text-white">{fmt(exam.start_time)}</span></div>
        <div><span className="text-gray-500 dark:text-slate-400">{t('exams.endTime')}:</span> <span className="text-gray-800 dark:text-white">{fmt(exam.end_time)}</span></div>
        <div><span className="text-gray-500 dark:text-slate-400">{t('exams.timeLimit')}:</span> <span className="text-gray-800 dark:text-white">{exam.time_limit_minutes ?? '—'} min</span></div>
        <div><span className="text-gray-500 dark:text-slate-400">{t('exams.allowedAttempts')}:</span> <span className="text-gray-800 dark:text-white">{exam.allowed_attempts}</span></div>
      </div>
      <div className="flex gap-2">
        {exam.is_national_cert && <Badge variant="purple">🎓 Sertifikat</Badge>}
        {exam.is_public && <Badge variant="green">🌐 Ommaviy</Badge>}
        {exam.leaderboard_visible && <Badge variant="amber">🏆 Reyting ommaviy</Badge>}
        {exam.show_errors && <Badge variant="blue">🔍 Xatolarni ko'rsatish</Badge>}
        {exam.grading_criteria_detail && (
          <Badge variant="blue"><BarChart2 size={11} className="inline mr-1" />{exam.grading_criteria_detail.name}</Badge>
        )}
      </div>
      {exam.banks?.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-medium text-gray-500 dark:text-slate-400 uppercase">{t('nav.questionBanks', 'Savollar bazalari')}</p>
            <button onClick={handleViewQuestions}
              className="text-xs font-semibold flex items-center gap-1 transition-colors"
              style={{ color: 'var(--coral)' }}>
              Savollarni ko'rish <HelpCircle size={12} />
            </button>
          </div>
          <div className="space-y-1">
            {exam.banks.map(b => (
              <div key={b.id} className="flex items-center justify-between bg-gray-50 dark:bg-slate-800 rounded-lg px-3 py-2 text-sm">
                <span className="text-gray-800 dark:text-white">{b.bank_name}</span>
                <span className="text-gray-500 dark:text-slate-400">{b.question_count} savol × {b.score_per_question} ball</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {exam.code && (
        <div className="flex items-center gap-3 bg-gray-50 dark:bg-slate-800/50 rounded-xl px-4 py-3 border border-gray-200 dark:border-slate-700">
          <div className="flex-1">
            <p className="text-xs font-medium text-gray-500 dark:text-slate-400 uppercase mb-1">Imtihon kodi</p>
            <CodeChip code={exam.code} />
          </div>
          {BOT_USERNAME && (
            <div className="flex flex-col items-end gap-1">
              <p className="text-xs font-medium text-gray-500 dark:text-slate-400 uppercase">Telegram havolasi</p>
              <TgLinkButton code={exam.code} />
            </div>
          )}
        </div>
      )}
      <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-100 dark:border-slate-800">
        <Button variant="secondary" onClick={onClose} className="flex-1">{t('common.close', 'Yopish')}</Button>
        <Button variant="secondary" onClick={onResults} className="flex-1">
          <Users size={14} /> O'quvchilar javoblari
        </Button>
        <Button variant="secondary" onClick={() => window.open(`${MAIN_URL}/exam/${exam.code}/leaderboard`, '_blank')} className="flex-1">
          <Trophy size={14} /> Reyting
        </Button>
        <PdfExportMenu exam={exam} className="flex-1" />
        <ShareButton exam={exam} className="flex-1" />
        <Button onClick={onEdit} className="flex-1">
          <Pencil size={14} /> {t('common.edit')}
        </Button>
      </div>
    </div>
  )
}

// ── Exam code copy chip ────────────────────────────────────────────────
function CodeChip({ code }) {
  const [copied, setCopied] = useState(false)
  if (!code) return <span className="secondary">—</span>
  function handleCopy(e) {
    e.stopPropagation()
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    })
  }
  return (
    <button
      onClick={handleCopy}
      title="Kodni nusxalash"
      className="flex items-center gap-1 rounded-lg px-2 py-0.5 text-xs font-mono font-semibold transition-all"
      style={{
        background: 'var(--bg-soft)', border: '1px solid var(--border)',
        color: copied ? 'var(--mint, #34d399)' : 'var(--ink-2)',
        cursor: 'pointer',
      }}
    >
      {code}
      {copied ? <Check size={11} /> : <Copy size={11} />}
    </button>
  )
}

// ── Telegram deep-link copy button ────────────────────────────────────
const BOT_USERNAME = import.meta.env.VITE_TELEGRAM_BOT_USERNAME || ''
const MAIN_URL     = import.meta.env.VITE_MAIN_URL || ''

function TgLinkButton({ code }) {
  const [copied, setCopied] = useState(false)
  if (!code || !BOT_USERNAME) return null
  function handleCopy(e) {
    e.stopPropagation()
    const link = `https://t.me/${BOT_USERNAME}?start=${code}`
    navigator.clipboard.writeText(link).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    })
  }
  return (
    <button
      onClick={handleCopy}
      title={copied ? 'Nusxalandi!' : 'Telegram havolasini nusxalash'}
      className="tbl-btn"
      style={{ color: copied ? 'var(--mint, #34d399)' : '#2ba5e0' }}
    >
      {copied ? <Check size={15} /> : <Send size={15} />}
    </button>
  )
}

// ── Share button — copies "title / date / teacher / link" to clipboard ─
function ShareButton({ exam, iconOnly, className }) {
  const [copied, setCopied] = useState(false)

  async function handleShare(e) {
    e.stopPropagation()
    const text = buildShareText(exam)
    if (navigator.share) {
      try { await navigator.share({ title: exam.title, text }) } catch { /* cancelled */ }
      return
    }
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    })
  }

  if (iconOnly) {
    return (
      <button onClick={handleShare} title={copied ? 'Nusxalandi!' : 'Ulashish'} className="tbl-btn tbl-edit">
        {copied ? <Check size={15} /> : <Share2 size={15} />}
      </button>
    )
  }
  return (
    <Button variant="secondary" onClick={handleShare} className={className}>
      {copied ? <><Check size={14} /> Nusxalandi!</> : <><Share2 size={14} /> Ulashish</>}
    </Button>
  )
}

// ── Page ──────────────────────────────────────────────────────────────
export default function ExamsPage() {
  const { t }                        = useTranslation()
  const navigate                     = useNavigate()
  const { user }                     = useAuth()
  const [exams,       setExams]      = useState([])
  const [allBanks,    setAllBanks]   = useState([])
  const [allCriteria, setAllCriteria]= useState([])
  const [allGroups,   setAllGroups]  = useState([])
  const [loading,     setLoading]    = useState(true)
  const [error,       setError]      = useState(null)
  const [panel,       setPanel]      = useState(null)
  const [search,      setSearch]     = useState('')
  const [page,        setPage]       = useState(1)
  const [confirm,     setConfirm]    = useState(null)

  function fetchExams() {
    setLoading(true)
    fetchAllPages(listExams)
      .then(setExams)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    fetchExams()
    fetchAllPages(listQuestionBanks).then(setAllBanks).catch(() => {})
    fetchAllPages(listGradingCriteria).then(setAllCriteria).catch(() => {})
    fetchAllPages(listGroups).then(setAllGroups).catch(() => {})
  }, [])

  async function openPanel(mode, exam) {
    if (mode === 'leaderboard' && exam) {
      window.open(`${MAIN_URL}/exam/${exam.code}/leaderboard`, '_blank')
      return
    }
    if (mode === 'results' && exam) {
      setPanel({ mode, exam })
      return
    }
    if ((mode === 'edit' || mode === 'view') && exam) {
      try {
        const { data } = await getExam(exam.code)
        setPanel({ mode, exam: data })
      } catch {
        setPanel({ mode, exam })
      }
    } else {
      setPanel({ mode })
    }
  }

  async function handleConfirmDelete() {
    await deleteExam(confirm)
    setConfirm(null)
    fetchExams()
  }

  const filtered = exams.filter(e => !search || e.title.toLowerCase().includes(search.toLowerCase()))
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE)
  const paginated  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
  useEffect(() => { setPage(1) }, [search])

  const panelTitle =
    panel?.mode === 'edit'        ? t('common.edit') :
    panel?.mode === 'view'        ? t('common.view', 'Ko\'rish') :
    panel?.mode === 'leaderboard' ? `🏆 ${t('exams.leaderboard', 'Reyting')}` :
    panel?.mode === 'results'     ? `👥 O'quvchilar javoblari` : ''

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('exams.title')}
        subtitle={`${filtered.length} ${t('common.total').toLowerCase()}`}
        actions={
          <Button icon={<Plus size={15} />} onClick={() => navigate('/exams/create')}>{t('exams.createNew')}</Button>
        }
      />

      <div className="flex items-center gap-3">
        <SearchInput
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={t('common.search', 'Qidirish...')}
        />
      </div>

      <div className="tbl-wrap">
        {loading ? (
          <div className="flex justify-center py-16"><Spinner size="lg" /></div>
        ) : error ? (
          <div className="p-6 text-red-600 text-sm">{error}</div>
        ) : filtered.length === 0 ? (
          <EmptyState icon={<ClipboardList size={48} />} title={t('common.noData')}
            action={<Button icon={<Plus size={15} />} onClick={() => navigate('/exams/create')}>{t('exams.createNew')}</Button>} />
        ) : (
          <>
            <table className="tbl">
              <thead>
                <tr>
                  <th>{t('exams.examTitle')}</th>
                  <th className="tc">Kod</th>
                  <th className="tc">{t('nav.questionBanks', 'Bazalar')}</th>
                  <th className="tc">{t('exams.questions', 'Savollar')}</th>
                  <th>{t('exams.startTime')}</th>
                  <th>{t('exams.endTime')}</th>
                  <th>{t('common.actions', 'Amallar')}</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map(exam => (
                  <tr key={exam.id}>
                    <td>
                      <div className="flex items-center gap-2">
                        <span className="primary">{exam.title}</span>
                        {exam.is_national_cert && <Badge variant="purple">🎓</Badge>}
                        {exam.is_public && <Badge variant="green">🌐</Badge>}
                      </div>
                    </td>
                    <td className="tc"><CodeChip code={exam.code} /></td>
                    <td className="tc"><span className="secondary">{exam.bank_count ?? '—'}</span></td>
                    <td className="tc"><span className="secondary">{exam.total_questions ?? '—'}</span></td>
                    <td><span className="secondary">{fmt(exam.start_time)}</span></td>
                    <td><span className="secondary">{fmt(exam.end_time)}</span></td>
                    <td className="tr">
                      <div className="flex items-center justify-end">
                        <RowActionsMenu
                          exam={exam}
                          onView={() => openPanel('view', exam)}
                          onResults={() => openPanel('results', exam)}
                          onLeaderboard={() => openPanel('leaderboard', exam)}
                          onEdit={() => openPanel('edit', exam)}
                          onDelete={() => setConfirm(exam.code)}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <TableFooter page={page} totalPages={totalPages} totalCount={filtered.length} onPageChange={setPage} />
          </>
        )}
      </div>

      {confirm !== null && (
        <ConfirmDialog
          title="O'chirishni tasdiqlang"
          message="Haqiqatan ham o'chirmoqchimisiz?"
          onConfirm={handleConfirmDelete}
          onCancel={() => setConfirm(null)}
        />
      )}

      <SlideOver open={!!panel} title={panelTitle} onClose={() => setPanel(null)} closeOnBackdrop={panel?.mode !== 'create' && panel?.mode !== 'edit'}>
        {panel?.mode === 'view' && panel.exam && (
          <ExamView
            exam={panel.exam}
            t={t}
            onClose={() => setPanel(null)}
            onEdit={() => openPanel('edit', panel.exam)}
            onResults={() => setPanel({ mode: 'results', exam: panel.exam })}
          />
        )}
        {panel?.mode === 'leaderboard' && panel.exam && (
          <LeaderboardPanel exam={panel.exam} t={t} onClose={() => setPanel(null)} />
        )}
        {panel?.mode === 'results' && panel.exam && (
          <ResultsPanel exam={panel.exam} t={t} onClose={() => setPanel(null)} />
        )}
        {panel?.mode === 'edit' && (
          <ExamForm
            key={panel.exam?.id}
            initial={panel.exam}
            allBanks={allBanks}
            allCriteria={allCriteria}
            allGroups={allGroups}
            setCriteria={setAllCriteria}
            defaultCriteriaId={user?.default_grading_criteria}
            t={t}
            onSaved={() => { setPanel(null); fetchExams() }}
            onCancel={() => setPanel(null)}
          />
        )}
      </SlideOver>
    </div>
  )
}

